import React, { useState, useEffect } from 'react';
import { Edit, X, Calendar } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const InvestmentSummaryCard = ({ investmentData, onEdit }) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [formData, setFormData] = useState({
    targetReturn: investmentData?.targetReturn || '',
    investmentTerm: investmentData?.investmentTerm || '',
    minimumInvestment: investmentData?.minimumInvestment || '',
    targetFundingDate: investmentData?.targetCloseDate || ''
  });

  const handleEditClick = () => {
    setShowOverlay(true);
  };

  const handleCloseOverlay = () => {
    setShowOverlay(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    // Call parent onEdit function with updated data
    if (onEdit) {
      onEdit(formData);
    }
    setShowOverlay(false);
  };

  // Update formData when investmentData prop changes
  React.useEffect(() => {
    setFormData({
      targetReturn: investmentData?.targetReturn || '',
      investmentTerm: investmentData?.investmentTerm || '',
      minimumInvestment: investmentData?.minimumInvestment || '',
      targetFundingDate: investmentData?.targetCloseDate || ''
    });
  }, [investmentData]);

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {/* Card Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Investment Summary
          </h2>
          <Button
            onClick={handleEditClick}
            variant="ghost"
            size="sm"
            className="text-gray-500 hover:text-gray-700 p-2"
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>

        {/* Investment Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">
              Target Return
            </p>
            <p className="text-gray-900 font-semibold text-lg">
              {investmentData?.targetReturn}
            </p>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">
              Investment Term
            </p>
            <p className="text-gray-900 font-semibold text-lg">
              {investmentData?.investmentTerm}
            </p>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">
              Minimum Investment
            </p>
            <p className="text-gray-900 font-semibold text-lg">
              {investmentData?.minimumInvestment}
            </p>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-500 mb-1">
              Target Close Date
            </p>
            <p className="text-gray-900 font-semibold text-lg">
              {investmentData?.targetCloseDate}
            </p>
          </div>
        </div>
      </div>
      {/* Overlay */}
      {showOverlay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Overlay Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Investment Summary
              </h3>
              <Button
                onClick={handleCloseOverlay}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Form Content */}
            <div className="p-6 space-y-4">
              {/* Target Return Field */}
              <Input
                label="Target Return"
                type="text"
                value={formData?.targetReturn}
                onChange={(e) => handleInputChange('targetReturn', e?.target?.value)}
                placeholder="e.g., 15-18% IRR"
                required
              />

              {/* Investment Term Field */}
              <Input
                label="Investment Term"
                type="text"
                value={formData?.investmentTerm}
                onChange={(e) => handleInputChange('investmentTerm', e?.target?.value)}
                placeholder="e.g., 5 years"
                required
              />

              {/* Minimum Investment Field */}
              <Input
                label="Minimum Investment"
                type="text"
                value={formData?.minimumInvestment}
                onChange={(e) => handleInputChange('minimumInvestment', e?.target?.value)}
                placeholder="e.g., $50,000"
                required
              />

              {/* Target Funding Date Field with Calendar Icon */}
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">
                  Target Funding Date
                  <span className="text-destructive ml-1">*</span>
                </label>
                <div className="relative">
                  <Input
                    type="date"
                    value={formData?.targetFundingDate}
                    onChange={(e) => handleInputChange('targetFundingDate', e?.target?.value)}
                    className="pr-10"
                    required
                  />
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Overlay Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <Button
                onClick={handleCloseOverlay}
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InvestmentSummaryCard;